import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { certificateAPI } from '../services/api';
import toast from 'react-hot-toast';

const MyCertificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');

  const { user } = useAuth();

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await certificateAPI.getMyCertificates();
      setCertificates(response.data.data.certificates);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      MINTED: 'badge-success',
      PENDING: 'badge-warning',
      REVOKED: 'badge-error',
      VERIFIED: 'badge-success'
    };
    return `badge ${statusClasses[status] || 'badge-info'}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredCertificates = certificates.filter(cert => {
    if (filter === 'ALL') return true;
    return cert.status === filter;
  });

  const handleRevoke = async (certificateId) => {
    if (!window.confirm('Are you sure you want to revoke this certificate? This action cannot be undone.')) {
      return;
    }

    try {
      const reason = prompt('Please provide a reason for revocation:');
      if (!reason) return;

      await certificateAPI.revokeCertificate(certificateId, reason);
      toast.success('Certificate revoked successfully');
      fetchCertificates();
    } catch (error) {
      console.error('Failed to revoke certificate:', error);
      toast.error('Failed to revoke certificate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="spinner"></div>
        <span className="ml-2">Loading certificates...</span>
      </div>
    );
  }

  return (
    <div className="my-certificates-page">
      <div className="container">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              {user.role === 'ADMIN' ? 'Issued Records' : 'My Certificates'}
            </h1>
          </div>

          {user.role === 'ADMIN' && (
            <Link to="/issue" className="btn btn-primary">
              Issue New Record
            </Link>
          )}
        </div>

        <div className="card mb-8">
          <div className="card-body">
            <div className="flex items-center gap-4">
              <span style={{ fontWeight: '500' }}>Filter:</span>
              <div className="flex gap-2">
                {['ALL', 'MINTED', 'PENDING', 'REVOKED'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`btn btn-sm ${filter === status ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {filteredCertificates.length > 0 ? (
          <div className="grid gap-4">
            {filteredCertificates.map((certificate) => (
              <div key={certificate._id} className="card">
                <div className="card-body">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                          {certificate.studentData?.name}
                        </h3>
                        <span className={getStatusBadge(certificate.status)}>
                          {certificate.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Degree</div>
                          <div>{certificate.academicData?.degree}</div>
                        </div>

                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>CGPA</div>
                          <div>{certificate.academicData?.overallCGPA}</div>
                        </div>

                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Year</div>
                          <div>{certificate.academicData?.graduationYear}</div>
                        </div>

                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Issued Date</div>
                          <div>{formatDate(certificate.issuedAt)}</div>
                        </div>
                      </div>

                      {certificate.tokenId && (
                        <div className="p-3 mb-4" style={{ backgroundColor: 'var(--background-color)', borderRadius: 'var(--border-radius)' }}>
                          <div style={{ fontSize: '0.875rem' }}>
                            <strong>Blockchain Token ID:</strong> #{certificate.tokenId}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2" style={{ minWidth: '150px' }}>
                      <Link
                        to={`/certificate/${certificate._id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View Record
                      </Link>

                      {user.role === 'ADMIN' && certificate.status === 'MINTED' && (
                        <button
                          onClick={() => handleRevoke(certificate._id)}
                          className="btn btn-sm"
                          style={{ backgroundColor: 'var(--error-color)', color: 'white' }}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center p-12">
              <h3>No digital records found</h3>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCertificates;